// src/pages/Home.ts

import "./Home.css";
import {searchMap} from "../searchMap";
import {useTheme} from "../context/useTheme";
import {useAuth} from "../context/useAuth";
import {renderHomeSearchSuggestions} from "../components/HomeSearchSuggestions";
import {renderProfileDropdown} from "../components/ProfileDropdown";
import {renderAIZone} from "./AIZone";

type HomeLanguage = "yo" | "ha" | "ig" | "en";

interface LocalizedText {
  yo: string;
  ha: string;
  ig: string;
  en: string;
}

interface HomeTopic {
  id: string;
  label: LocalizedText;
  icon: string;
}

interface HomeNewsArticle {
  id: string;
  category: string;
  title: LocalizedText;
  summary: LocalizedText;
  source: string;
  time: LocalizedText;
  image: string;
  url: string;
  featured?: boolean;
}

const HOME_LANGUAGE_STORAGE_KEY = "ayesearch-home-language";

const HOME_LANGUAGES: Array<{
  id: HomeLanguage;
  name: string;
  shortName: string;
}> = [
  {id: "yo", name: "Yorùbá", shortName: "YOR"},
  {id: "ha", name: "Hausa", shortName: "HAU"},
  {id: "ig", name: "Igbo", shortName: "IGB"},
  {id: "en", name: "English", shortName: "ENG"},
];

let activeHomeLanguage: HomeLanguage = readStoredHomeLanguage();
let activeHomeTopic = "top";

function readStoredHomeLanguage(): HomeLanguage {
  try {
    const storedLanguage = window.localStorage.getItem(
      HOME_LANGUAGE_STORAGE_KEY,
    );

    if (
      storedLanguage === "yo" ||
      storedLanguage === "ha" ||
      storedLanguage === "ig" ||
      storedLanguage === "en"
    ) {
      return storedLanguage;
    }
  } catch {
    // localStorage can be unavailable in private/restricted browser contexts.
  }

  return "yo";
}

function saveHomeLanguage(language: HomeLanguage): void {
  activeHomeLanguage = language;

  try {
    window.localStorage.setItem(HOME_LANGUAGE_STORAGE_KEY, language);
  } catch {
    // Keep the in-memory selection when localStorage is unavailable.
  }
}

function textFor(value: LocalizedText): string {
  return value[activeHomeLanguage] || value.en;
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function normalizeQuery(q: string): string {
  return q
    .toLowerCase()
    .trim()
    .replace(/\.(jpg|jpeg|png|gif|webp)$/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function getProfileColor(email: string): string {
  const colors = ["#4285f4", "#ea4335", "#fbbc05", "#34a853", "#a142f4"];
  let hash = 0;

  for (let i = 0; i < email.length; i += 1) {
    hash = email.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
}

function getThemeLabel(theme: string): string {
  switch (theme) {
    case "device":
      return textFor(UI_TEXT.device);
    case "white":
      return textFor(UI_TEXT.white);
    case "light":
      return textFor(UI_TEXT.silver);
    case "dark":
      return textFor(UI_TEXT.dark);
    default:
      return textFor(UI_TEXT.device);
  }
}

const UI_TEXT = {
  about: {
    yo: "Nípa",
    ha: "Game da",
    ig: "Banyere",
    en: "About",
  },
  store: {
    yo: "Ilé ìtajà",
    ha: "Shago",
    ig: "Ụlọ ahịa",
    en: "Store",
  },
  theme: {
    yo: "Àkòrí",
    ha: "Jigo",
    ig: "Isiokwu",
    en: "Theme",
  },
  device: {
    yo: "Ẹrọ",
    ha: "Na'ura",
    ig: "Ngwaọrụ",
    en: "Device",
  },
  white: {
    yo: "Funfun",
    ha: "Fari",
    ig: "Ọcha",
    en: "White",
  },
  silver: {
    yo: "Fàdákà",
    ha: "Azurfa",
    ig: "Ọlaọcha",
    en: "Silver",
  },
  dark: {
    yo: "Òkùnkùn",
    ha: "Duhu",
    ig: "Ọchịchịrị",
    en: "Dark",
  },
  images: {
    yo: "Àwòrán",
    ha: "Hotuna",
    ig: "Foto",
    en: "Images",
  },
  signIn: {
    yo: "Wọlé",
    ha: "Shiga",
    ig: "Banye",
    en: "Sign In",
  },
  all: {
    yo: "Gbogbo",
    ha: "Duka",
    ig: "Niile",
    en: "All",
  },
  openApps: {
    yo: "Ṣí àwọn app",
    ha: "Buɗe apps",
    ig: "Mepee ngwa",
    en: "Open apps",
  },
  openNavigation: {
    yo: "Ṣí àkójọ",
    ha: "Buɗe kewayawa",
    ig: "Mepee nsogharị",
    en: "Open navigation",
  },
  discover: {
    yo: "Ṣàwárí",
    ha: "Gano",
    ig: "Chọpụta",
    en: "Discover",
  },
  newsForYou: {
    yo: "Ìròyìn fún ọ",
    ha: "Labarai domin ku",
    ig: "Akụkọ maka gị",
    en: "News for you",
  },
  moreNews: {
    yo: "Ìròyìn síi",
    ha: "Ƙarin labarai",
    ig: "Akụkọ ndị ọzọ",
    en: "More news",
  },
  topStory: {
    yo: "Ìròyìn pàtàkì",
    ha: "Babban labari",
    ig: "Akụkọ kacha mkpa",
    en: "Top story",
  },
  chooseLanguage: {
    yo: "Yan èdè",
    ha: "Zaɓi harshe",
    ig: "Họrọ asụsụ",
    en: "Choose language",
  },
  closeLanguage: {
    yo: "Pa yíyan èdè",
    ha: "Rufe zaɓin harshe",
    ig: "Mechie nhọrọ asụsụ",
    en: "Close language picker",
  },
  searchPlaceholder: {
    yo: "Ṣàwárí lórí ayélujára...",
    ha: "Bincika yanar gizo...",
    ig: "Chọọ na webụ...",
    en: "Search the web...",
  },
  searchButton: {
    yo: "AyeSearch",
    ha: "AyeSearch",
    ig: "AyeSearch",
    en: "AyeSearch",
  },
} satisfies Record<string, LocalizedText>;

const APPS_LIST = [
  {
    name: "VibeJoos",
    icon: "▶",
    color: "#ff6b6b",
    image: "",
  },
  {
    name: "AMailMe",
    icon: "",
    color: "transparent",
    image: "/static/ayesearch/Amailme_Icon.png",
  },
  {
    name: "OreChat",
    icon: "",
    color: "transparent",
    image: "/static/ayesearch/OreChat_Icon.png",
  },
  {
    name: "ADrive",
    icon: "☁",
    color: "#ffd43b",
    image: "",
  },
  {
    name: "ADocs",
    icon: "📄",
    color: "#845ef7",
    image: "",
  },
  {
    name: "ANote",
    icon: "📝",
    color: "#f06595",
    image: "",
  },
];

const HOME_TOPICS: HomeTopic[] = [
  {
    id: "top",
    label: {yo: "Ìròyìn pàtàkì", ha: "Manyan labarai", ig: "Akụkọ kachasị", en: "Top stories"},
    icon: "🔥",
  },
  {
    id: "wiki",
    label: {yo: "Aikipedia", ha: "Aikipedia", ig: "Aikipedia", en: "Aikipedia"},
    icon: "📚",
  },
  {
    id: "games",
    label: {yo: "Eré", ha: "Wasanni", ig: "Egwuregwu", en: "Games"},
    icon: "🎮",
  },
  {
    id: "anime",
    label: {yo: "Anime", ha: "Anime", ig: "Anime", en: "Anime"},
    icon: "✨",
  },
  {
    id: "technology",
    label: {yo: "Ìmọ̀ ẹ̀rọ", ha: "Fasaha", ig: "Teknụzụ", en: "Technology"},
    icon: "💻",
  },
  {
    id: "sports",
    label: {yo: "Eré ìdárayá", ha: "Wasanni", ig: "Egwuregwu", en: "Sports"},
    icon: "🏀",
  },
  {
    id: "business",
    label: {yo: "Iṣòwò", ha: "Kasuwanci", ig: "Azụmahịa", en: "Business"},
    icon: "📈",
  },
  {
    id: "entertainment",
    label: {yo: "Eré ìnàjú", ha: "Nishaɗi", ig: "Ntụrụndụ", en: "Entertainment"},
    icon: "🎬",
  },
];

const HOME_NEWS_ARTICLES: HomeNewsArticle[] = [
  {
    id: "cnn-headline",
    category: "top",
    title: {
      yo: "Àwọn ìròyìn pàtàkì ń ṣẹlẹ̀ káàkiri Amẹ́ríkà àti gbogbo ayé",
      ha: "Manyan labarai na ci gaba da faruwa a Amurka da sauran duniya",
      ig: "Akụkọ ndị dị mkpa na-emepe emepe na United States na gburugburu ụwa",
      en: "Major stories developing across the United States and around the world",
    },
    summary: {
      yo: "Tẹ̀lé ìròyìn pajawiri, àyẹ̀wò, ìròyìn láàyè àti àwọn ìṣẹ̀lẹ̀ tó ń darí ìjíròrò òní.",
      ha: "Bi diddigin labaran gaggawa, sharhi, rahotanni kai tsaye da batutuwan da ke jagorantar tattaunawar yau.",
      ig: "Soro akụkọ ngwa ngwa, nyocha, akụkọ ndụ na ihe ndị na-akpụ mkparịta ụka taa.",
      en: "Follow breaking news, analysis, live reports and the stories shaping today's conversation.",
    },
    source: "CNN",
    time: {yo: "ìṣẹ́jú 12 sẹ́yìn", ha: "minti 12 da suka wuce", ig: "nkeji 12 gara aga", en: "12 min ago"},
    image: "https://images.unsplash.com/photo-1495020689067-958852a7765e?auto=format&fit=crop&w=1400&q=82",
    url: "https://www.cnn.com/",
    featured: true,
  },
  {
    id: "bbc-world",
    category: "top",
    title: {
      yo: "Àwọn olórí ayé fèsì sí àwọn ìṣẹ̀lẹ̀ tuntun ní àgbáyé",
      ha: "Shugabannin duniya sun mayar da martani ga sabbin abubuwan da ke faruwa",
      ig: "Ndị isi ụwa zara mmepe mba ụwa kachasị ọhụrụ",
      en: "World leaders respond to the latest international developments",
    },
    summary: {
      yo: "Ìròyìn láti Yúróòpù, Éṣíà, Áfíríkà àti Amẹ́ríkà bí àwọn ìṣẹ̀lẹ̀ ń bá a lọ.",
      ha: "Rahotanni daga Turai, Asiya, Afirka da Amurka yayin da manyan al'amura ke ci gaba.",
      ig: "Akụkọ sitere Europe, Asia, Africa na Americas ka nnukwu ihe omume na-aga n'ihu.",
      en: "Reporting from Europe, Asia, Africa and the Americas as major events continue to unfold.",
    },
    source: "BBC News",
    time: {yo: "ìṣẹ́jú 28 sẹ́yìn", ha: "minti 28 da suka wuce", ig: "nkeji 28 gara aga", en: "28 min ago"},
    image: "https://images.unsplash.com/photo-1529107386315-e1a2ed48a620?auto=format&fit=crop&w=1000&q=82",
    url: "https://www.bbc.com/news",
  },
  {
    id: "reuters-world",
    category: "top",
    title: {
      yo: "Ọjà àti ìjọba fèsì sí ọjọ́ ìròyìn àgbáyé tó ń yí padà kíákíá",
      ha: "Kasuwanni da gwamnatoci sun mayar da martani ga ranar labaran duniya mai saurin sauyawa",
      ig: "Ahịa na gọọmentị zara ụbọchị akụkọ ụwa na-agbanwe ngwa ngwa",
      en: "Markets and governments react to a fast-moving day of global news",
    },
    summary: {
      yo: "Ìlànà, ọrọ̀ ajé àti ọ̀ràn àgbáyé ló ṣáájú àwọn ìròyìn tuntun láti ilé iṣẹ́ ìròyìn ńlá.",
      ha: "Manufofi, tattalin arziki da harkokin duniya sun jagoranci sabbin rahotanni daga manyan gidajen labarai.",
      ig: "Iwu, akụ na ụba na mmekọrịta mba ụwa na-eduga mmelite sitere n'ụlọ akụkọ ukwu.",
      en: "Policy, economics and international affairs lead the latest updates from major newsrooms.",
    },
    source: "Reuters",
    time: {yo: "ìṣẹ́jú 37 sẹ́yìn", ha: "minti 37 da suka wuce", ig: "nkeji 37 gara aga", en: "37 min ago"},
    image: "https://images.unsplash.com/photo-1585829365295-ab7cd400c167?auto=format&fit=crop&w=1000&q=82",
    url: "https://www.reuters.com/world/",
  },
  {
    id: "ap-top",
    category: "top",
    title: {
      yo: "Àwọn àkọlé pàtàkì àti ìròyìn láàyè láti gbogbo orílẹ̀-èdè",
      ha: "Manyan kanun labarai da sabbin rahotanni kai tsaye daga ko'ina cikin ƙasa",
      ig: "Isi akụkọ na mmelite ndụ sitere n'akụkụ niile nke mba ahụ",
      en: "Top headlines and live updates from across the country",
    },
    summary: {
      yo: "Ìwòye kíákíá sí àwọn ìròyìn orílẹ̀-èdè tí gbogbo ènìyàn ń fiyè sí lónìí.",
      ha: "Takaitaccen duba manyan labaran ƙasa da suka fi jan hankali a yau.",
      ig: "Nlele ngwa ngwa n'akụkọ mba ndị na-enweta nlebara anya kachasị taa.",
      en: "A quick look at the national stories receiving the most attention today.",
    },
    source: "AP News",
    time: {yo: "ìṣẹ́jú 46 sẹ́yìn", ha: "minti 46 da suka wuce", ig: "nkeji 46 gara aga", en: "46 min ago"},
    image: "https://images.unsplash.com/photo-1504711434969-e33886168f5c?auto=format&fit=crop&w=1000&q=82",
    url: "https://apnews.com/",
  },
  {
    id: "wiki-feature",
    category: "wiki",
    title: {
      yo: "Ṣàwárí ìtàn, sáyẹ́ǹsì àti àṣà tí a yàn fún òní",
      ha: "Bincika fitattun batutuwan tarihi, kimiyya da al'adu na yau",
      ig: "Nyochaa akụkọ ihe mere eme, sayensị na omenala ahọpụtara taa",
      en: "Explore today's featured history, science and culture articles",
    },
    summary: {
      yo: "Ṣàwárí àwọn ìṣẹ̀lẹ̀, ènìyàn àti kókó pàtàkì nípasẹ̀ ìmọ̀ ìwé-ìtàn.",
      ha: "Gano muhimman abubuwa, mutane da batutuwa ta hanyar ilimin kundin sani.",
      ig: "Chọpụta ihe omume, ndị mmadụ na isiokwu ama ama site n'ihe ọmụma encyclopedic.",
      en: "Discover notable events, people and subjects through encyclopedic knowledge.",
    },
    source: "Aikipedia",
    time: {yo: "Òní", ha: "Yau", ig: "Taa", en: "Today"},
    image: "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?auto=format&fit=crop&w=1200&q=82",
    url: "https://aikipedia.ayesearch.com/",
  },
  {
    id: "wiki-history",
    category: "wiki",
    title: {
      yo: "Ní ọjọ́ yìí: àwọn àkókò tó yí ìtàn padà",
      ha: "A wannan rana: lokutan da suka sauya tarihi",
      ig: "N'ụbọchị a: oge ndị gbanwere akụkọ ihe mere eme",
      en: "On this day: moments that changed history",
    },
    summary: {
      yo: "Wo àwọn ìṣẹ̀lẹ̀, ìṣàwárí, ìbí àti àṣeyọrí pàtàkì tó ní í ṣe pẹ̀lú ọjọ́ òní.",
      ha: "Duba muhimman abubuwan tarihi, bincike, haihuwa da nasarorin da suka shafi ranar yau.",
      ig: "Lelee ihe omume, nchọpụta, ọmụmụ na ihe ịga nke ọma jikọrọ ụbọchị taa.",
      en: "Browse important events, discoveries, births and milestones associated with today's date.",
    },
    source: "Aikipedia",
    time: {yo: "Òní", ha: "Yau", ig: "Taa", en: "Today"},
    image: "https://images.unsplash.com/photo-1461360370896-922624d12aa1?auto=format&fit=crop&w=1000&q=82",
    url: "https://aikipedia.ayesearch.com/",
  },
  {
    id: "games-feature",
    category: "games",
    title: {
      yo: "Àwọn eré tuntun, ìmúdójúìwọ̀n àti ìkéde ńlá láti inú ilé iṣẹ́ eré",
      ha: "Sabbin wasanni, sabuntawa da manyan sanarwa daga masana'antar wasa",
      ig: "Mwepụta ọhụrụ, mmelite na nnukwu ọkwa sitere n'ụwa egwuregwu",
      en: "New releases, updates and major announcements from gaming",
    },
    summary: {
      yo: "Mọ ohun tuntun nípa eré console, fóònù àti PC láti gbogbo ilé iṣẹ́ náà.",
      ha: "Samu sabbin labarai kan wasannin console, wayar hannu da PC daga ko'ina cikin masana'antar.",
      ig: "Soro akụkọ egwuregwu console, ekwentị na PC sitere n'akụkụ niile nke ụlọ ọrụ ahụ.",
      en: "Catch up on console, mobile and PC gaming news from across the industry.",
    },
    source: "IGN",
    time: {yo: "wákàtí 2 sẹ́yìn", ha: "awa 2 da suka wuce", ig: "awa 2 gara aga", en: "2 hrs ago"},
    image: "https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&w=1200&q=82",
    url: "https://www.ign.com/",
  },
  {
    id: "games-second",
    category: "games",
    title: {
      yo: "Àwọn studio ṣàfihàn àwọn eré tí àwọn eléré yóò máa sọ̀rọ̀ nípa wọn lẹ́yìn",
      ha: "Studiyo suna nuna wasannin da 'yan wasa za su yi magana a kansu nan gaba",
      ig: "Ụlọ ọrụ na-egosi egwuregwu ndị egwuregwu ga-ekwu maka ha n'oge na-abịa",
      en: "Studios preview the games players will be talking about next",
    },
    summary: {
      yo: "Trailer, àyẹ̀wò àti ìmúdójúìwọ̀n ọjọ́ ìtẹ̀jáde fún àwọn eré tó ń bọ̀.",
      ha: "Traila, bita da sabbin ranakun fitar da wasannin da ke tafe.",
      ig: "Trailers, nyocha na mmelite ụbọchị mwepụta maka egwuregwu na-abịa.",
      en: "Trailers, reviews and release-date updates from upcoming titles.",
    },
    source: "GameSpot",
    time: {yo: "wákàtí 3 sẹ́yìn", ha: "awa 3 da suka wuce", ig: "awa 3 gara aga", en: "3 hrs ago"},
    image: "https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&w=1000&q=82",
    url: "https://www.gamespot.com/",
  },
  {
    id: "anime-feature",
    category: "anime",
    title: {
      yo: "Àwọn anime tuntun àti ohun pàtàkì lórí streaming tó yẹ kí o wò",
      ha: "Sabbin anime da abubuwan kallo masu muhimmanci a streaming",
      ig: "Mwepụta anime na ihe pụrụ iche na streaming kwesịrị ilele",
      en: "Anime releases and streaming highlights worth watching",
    },
    summary: {
      yo: "Àwọn series tó ń bọ̀, àwọn ayanfẹ́ tó ń padà àti ìròyìn eré ìnàjú fún olólùfẹ́ anime.",
      ha: "Jerin da ke tafe, tsofaffin abubuwan so da labaran nishaɗi ga masoya anime.",
      ig: "Usoro na-abịa, ndị ọkacha mmasị na-alọghachi na mmelite ntụrụndụ maka ndị hụrụ anime n'anya.",
      en: "Upcoming series, returning favorites and entertainment updates for anime fans.",
    },
    source: "Crunchyroll News",
    time: {yo: "wákàtí 3 sẹ́yìn", ha: "awa 3 da suka wuce", ig: "awa 3 gara aga", en: "3 hrs ago"},
    image: "https://images.unsplash.com/photo-1578632767115-351597cf2477?auto=format&fit=crop&w=1200&q=82",
    url: "https://www.crunchyroll.com/news",
  },
  {
    id: "anime-second",
    category: "anime",
    title: {
      yo: "Àwọn adaptation àti series tó ń padà tí a ń retí jù lọ ní àsìkò yìí",
      ha: "Fitattun adaptations da jerin da ke dawowa da ake jira a wannan kakar",
      ig: "Adaptations na usoro na-alọghachi kacha atụ anya n'oge a",
      en: "The season's most anticipated adaptations and returning series",
    },
    summary: {
      yo: "Àkójọpọ̀ ìkéde, ìbẹ̀rẹ̀ àti àwọn franchise tí àwọn olólùfẹ́ fẹ́ràn.",
      ha: "Takaitaccen jerin sanarwa, fara nunawa da fitattun franchises.",
      ig: "Nchịkọta ọkwa, premiere na franchises ndị fan hụrụ n'anya.",
      en: "A roundup of announcements, premieres and fan-favorite franchises.",
    },
    source: "Anime News Network",
    time: {yo: "wákàtí 4 sẹ́yìn", ha: "awa 4 da suka wuce", ig: "awa 4 gara aga", en: "4 hrs ago"},
    image: "https://images.unsplash.com/photo-1607604276583-eef5d076aa5f?auto=format&fit=crop&w=1000&q=82",
    url: "https://www.animenewsnetwork.com/",
  },
  {
    id: "tech-feature",
    category: "technology",
    title: {
      yo: "Ọgbọ́n àtọwọ́dá ń bá a lọ láti yí ìmọ̀ ẹ̀rọ oníbàárà padà",
      ha: "Basirar wucin gadi na ci gaba da sauya fasahar masu amfani",
      ig: "Amamihe wuru ewu na-aga n'ihu ịgbanwe teknụzụ ndị ahịa",
      en: "Artificial intelligence continues to reshape consumer technology",
    },
    summary: {
      yo: "Àwọn ọja AI tuntun ń yí bí ènìyàn ṣe ń wá, bá ara wọn sọ̀rọ̀ àti ṣẹ̀dá lórí ayélujára.",
      ha: "Sabbin kayayyakin AI suna sauya yadda mutane ke bincike, sadarwa da ƙirƙira a yanar gizo.",
      ig: "Ngwaahịa AI ọhụrụ na-agbanwe otu ndị mmadụ si achọ, na-ekwurịta okwu ma na-emepụta n'ịntanetị.",
      en: "New AI products are changing how people search, communicate and create online.",
    },
    source: "The Verge",
    time: {yo: "wákàtí 1 sẹ́yìn", ha: "awa 1 da ta wuce", ig: "awa 1 gara aga", en: "1 hr ago"},
    image: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1200&q=82",
    url: "https://www.theverge.com/tech",
  },
  {
    id: "tech-second",
    category: "technology",
    title: {
      yo: "Àwọn ẹrọ tuntun, software tuntun àti ìkéde startup",
      ha: "Sabbin na'urori, sakin software da sanarwar startup",
      ig: "Ngwaọrụ ọhụrụ, mwepụta sọftụwia na ọkwa startup",
      en: "The latest devices, software releases and startup announcements",
    },
    summary: {
      yo: "Ìwòye sí àwọn ọja àti ilé iṣẹ́ tó ń ní ipa lórí ìgbì tuntun ti kọ̀ǹpútà.",
      ha: "Duba kayayyaki da kamfanonin da ke tasiri ga sabon zamani na kwamfuta.",
      ig: "Nlele ngwaahịa na ụlọ ọrụ ndị na-emetụta ebili mmiri ọhụrụ nke kọmputa.",
      en: "A look at the products and companies influencing the next wave of computing.",
    },
    source: "TechCrunch",
    time: {yo: "wákàtí 2 sẹ́yìn", ha: "awa 2 da suka wuce", ig: "awa 2 gara aga", en: "2 hrs ago"},
    image: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1000&q=82",
    url: "https://techcrunch.com/",
  },
  {
    id: "sports-feature",
    category: "sports",
    title: {
      yo: "Àwọn ẹgbẹ́ ń mura fún ètò kíkún míì ti àwọn eré ìdárayá ńlá",
      ha: "Ƙungiyoyi suna shirin wani cikakken jadawalin manyan wasanni",
      ig: "Otu dị iche iche na-akwadebe maka usoro egwuregwu ukwu ọzọ",
      en: "Teams prepare for another full schedule of major sporting events",
    },
    summary: {
      yo: "Àbájáde, ìtàn eléré àti ìdàgbàsókè league láti basketball, football àti bẹ́ẹ̀ bẹ́ẹ̀ lọ.",
      ha: "Sakamako, labaran 'yan wasa da ci gaban lig daga kwando, ƙwallon ƙafa da sauransu.",
      ig: "Akara, akụkọ ndị egwuregwu na mmepe league sitere basketball, football na ndị ọzọ.",
      en: "Scores, player stories and league developments from basketball, football and more.",
    },
    source: "ESPN",
    time: {yo: "ìṣẹ́jú 36 sẹ́yìn", ha: "minti 36 da suka wuce", ig: "nkeji 36 gara aga", en: "36 min ago"},
    image: "https://images.unsplash.com/photo-1461896836934-ffe607ba8211?auto=format&fit=crop&w=1200&q=82",
    url: "https://www.espn.com/",
  },
  {
    id: "sports-second",
    category: "sports",
    title: {
      yo: "Àwọn eléré àti olùkọ́ni fèsì lẹ́yìn alẹ́ ìdíje tó kún fún ìyàlẹ́nu",
      ha: "'Yan wasa da masu horarwa sun mayar da martani bayan daren gasa mai ban mamaki",
      ig: "Ndị egwuregwu na ndị nkuzi zara mgbe abalị asọmpi dị egwu gasịrị",
      en: "Players and coaches react after a dramatic night of competition",
    },
    summary: {
      yo: "Àwọn àkókò pàtàkì, ipò àti àyẹ̀wò láti inú àwọn eré tó tóbi jù.",
      ha: "Fitattun lokuta, matsayi da sharhi daga manyan wasanni.",
      ig: "Ihe ngosi, standings na nyocha sitere n'egwuregwu ndị kachasị ukwuu.",
      en: "Highlights, standings and analysis from the biggest games.",
    },
    source: "CBS Sports",
    time: {yo: "ìṣẹ́jú 54 sẹ́yìn", ha: "minti 54 da suka wuce", ig: "nkeji 54 gara aga", en: "54 min ago"},
    image: "https://images.unsplash.com/photo-1546519638-68e109498ffc?auto=format&fit=crop&w=1000&q=82",
    url: "https://www.cbssports.com/",
  },
  {
    id: "business-feature",
    category: "business",
    title: {
      yo: "Ọjà àgbáyé ń wo inflation, oṣùwọ̀n èlé àti èrè ilé iṣẹ́ ìmọ̀ ẹ̀rọ",
      ha: "Kasuwannin duniya na sa ido kan hauhawar farashi, kuɗin ruwa da ribar kamfanonin fasaha",
      ig: "Ahịa ụwa na-ele inflation, ọmụrụ nwa na ego ụlọ ọrụ teknụzụ",
      en: "Global markets watch inflation, interest rates and technology earnings",
    },
    summary: {
      yo: "Àwọn afowopaowo ń tọ́pa data ọrọ̀ ajé àti àbájáde ilé iṣẹ́ bí ọjà ṣe ń yí.",
      ha: "Masu zuba jari suna sa ido kan bayanan tattalin arziki da sakamakon kamfanoni yayin da kasuwanni ke motsi.",
      ig: "Ndị na-etinye ego na-enyocha data akụ na ụba na nsonaazụ ụlọ ọrụ ka ahịa na-agagharị.",
      en: "Investors are monitoring economic data and corporate results as markets move.",
    },
    source: "Reuters",
    time: {yo: "ìṣẹ́jú 41 sẹ́yìn", ha: "minti 41 da suka wuce", ig: "nkeji 41 gara aga", en: "41 min ago"},
    image: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=1200&q=82",
    url: "https://www.reuters.com/business/",
  },
  {
    id: "business-second",
    category: "business",
    title: {
      yo: "Àwọn ilé iṣẹ́ ṣàlàyé ìgbésẹ̀ tó kàn bí ìbéèrè oníbàárà ṣe ń yí",
      ha: "Kamfanoni sun bayyana matakan gaba yayin da buƙatar masu amfani ke sauyawa",
      ig: "Ụlọ ọrụ na-akọwa nzọụkwụ ha ọzọ ka ọchịchọ ndị ahịa na-agbanwe",
      en: "Companies outline their next moves as consumer demand shifts",
    },
    summary: {
      yo: "Ètò ilé iṣẹ́, iṣẹ́ àti ọrọ̀ ajé ṣì jẹ́ ohun pàtàkì fún àwọn olórí iṣowo.",
      ha: "Dabarun kamfani, ayyuka da tattalin arziki suna ci gaba da zama muhimman batutuwa ga shugabannin kasuwanci.",
      ig: "Atụmatụ ụlọ ọrụ, ọrụ na akụ na ụba ka dị n'etiti uche ndị isi azụmahịa.",
      en: "Corporate strategy, jobs and the economy remain in focus for business leaders.",
    },
    source: "CNBC",
    time: {yo: "wákàtí 1 sẹ́yìn", ha: "awa 1 da ta wuce", ig: "awa 1 gara aga", en: "1 hr ago"},
    image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1000&q=82",
    url: "https://www.cnbc.com/business/",
  },
  {
    id: "entertainment-feature",
    category: "entertainment",
    title: {
      yo: "Streaming, fíìmù àti tẹlifíṣọ̀n ló ṣáájú ìjíròrò eré ìnàjú òní",
      ha: "Streaming, fina-finai da talabijin sun jagoranci tattaunawar nishaɗi ta yau",
      ig: "Streaming, ihe nkiri na telivishọn na-eduga mkparịta ụka ntụrụndụ taa",
      en: "Streaming, movies and television lead today's entertainment conversation",
    },
    summary: {
      yo: "Àwọn studio àti pẹpẹ streaming ń kéde iṣẹ́ tuntun àti ìtẹ̀jáde tó ń bọ̀.",
      ha: "Studiyo da dandamalin streaming suna sanar da sabbin ayyuka da fitowar da ke tafe.",
      ig: "Ụlọ ọrụ na nyiwe streaming na-ekwupụta ọrụ ọhụrụ na mwepụta na-abịa.",
      en: "Studios and streaming platforms announce new projects and upcoming releases.",
    },
    source: "Variety",
    time: {yo: "wákàtí 1 sẹ́yìn", ha: "awa 1 da ta wuce", ig: "awa 1 gara aga", en: "1 hr ago"},
    image: "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?auto=format&fit=crop&w=1200&q=82",
    url: "https://variety.com/",
  },
  {
    id: "entertainment-second",
    category: "entertainment",
    title: {
      yo: "Trailer tuntun àti ìròyìn yíyan oṣèré dé fún àwọn iṣẹ́ ńlá tó ń bọ̀",
      ha: "Sabbin trailers da labaran zaɓen 'yan wasa sun zo don manyan ayyukan da ke tafe",
      ig: "Trailers ọhụrụ na akụkọ casting bịarutere maka nnukwu mwepụta na-abịa",
      en: "New trailers and casting news arrive for major upcoming releases",
    },
    summary: {
      yo: "Àwọn ìmúdójúìwọ̀n tuntun láti fíìmù, tẹlifíṣọ̀n, orin àti ayé àwọn gbajúgbajà.",
      ha: "Sabbin bayanai daga fina-finai, talabijin, kiɗa da al'adun mashahurai.",
      ig: "Mmelite kachasị ọhụrụ sitere n'ihe nkiri, telivishọn, egwu na omenala ndị ama ama.",
      en: "The latest updates from film, television, music and celebrity culture.",
    },
    source: "Entertainment Weekly",
    time: {yo: "wákàtí 2 sẹ́yìn", ha: "awa 2 da suka wuce", ig: "awa 2 gara aga", en: "2 hrs ago"},
    image: "https://images.unsplash.com/photo-1485846234645-a62644f84728?auto=format&fit=crop&w=1000&q=82",
    url: "https://ew.com/",
  },
];

function generateAppsMenuHtml(): string {
  return `
    <div class="apps-menu hidden" id="apps-dropdown-panel">
      <div class="apps-menu-grid">
        ${APPS_LIST.map(
          (app) => `
            <div class="app-item">
              ${
                app.image
                  ? `<img
                      class="app-icon app-icon-image"
                      src="${app.image}"
                      alt="${escapeHtml(app.name)}"
                    />`
                  : `<div
                      class="app-icon"
                      style="background-color: ${app.color}"
                      aria-hidden="true"
                    >${app.icon}</div>`
              }
              <span>${escapeHtml(app.name)}</span>
            </div>
          `,
        ).join("")}
      </div>
      <div class="apps-download-hub">
        <div class="apps-download-hub-header">
          <img
            class="apps-browser-icon"
            src="/static/ayesearch/Ayesearch_Icon.png"
            alt="AyeSearch"
          />
          <div class="apps-browser-copy">
            <strong>Get AyeSearch</strong>
            <span>Choose mobile or desktop browser.</span>
          </div>
        </div>

        <div class="apps-download-platforms">
          <div class="apps-download-platform">
            <span class="apps-download-kicker">Mobile app</span>
            <strong>iOS / Android</strong>
            <a
              class="apps-browser-download-btn"
              href="https://ayesearch.com/downloads/update"
              aria-label="Download AyeSearch for iOS or Android"
            >
              Download
            </a>
          </div>

          <div class="apps-download-platform">
            <span class="apps-download-kicker">AyeSearch Browser</span>
            <strong>Mac / Windows</strong>
            <a
              class="apps-browser-download-btn"
              href="https://ayesearch.com/downloads/update"
              aria-label="Download AyeSearch Browser for Mac or Windows"
            >
              Download
            </a>
          </div>
        </div>
      </div>
    </div>
  `;
}

function generateTopicTabsHtml(): string {
  return HOME_TOPICS.map(
    (topic) => `
      <button
        class="home-topic-chip ${topic.id === activeHomeTopic ? "active" : ""}"
        type="button"
        data-topic="${topic.id}"
        aria-pressed="${topic.id === activeHomeTopic ? "true" : "false"}"
      >
        <span class="home-topic-icon" aria-hidden="true">${topic.icon}</span>
        <span>${escapeHtml(textFor(topic.label))}</span>
      </button>
    `,
  ).join("");
}

function generateLanguagePickerHtml(): string {
  const selectedLanguage =
    HOME_LANGUAGES.find((language) => language.id === activeHomeLanguage) ??
    HOME_LANGUAGES[0];

  return `
    <div class="home-language-control" id="home-language-control">
      <button
        class="home-language-btn"
        id="home-language-btn"
        type="button"
        aria-haspopup="dialog"
        aria-expanded="false"
        aria-controls="home-language-popup"
      >
        <span class="home-language-globe" aria-hidden="true">🌐</span>
        <span id="home-language-button-label">${selectedLanguage.name}</span>
        <span class="home-language-chevron" aria-hidden="true">▾</span>
      </button>

      <div class="home-language-backdrop hidden" id="home-language-backdrop"></div>

      <div
        class="home-language-popup hidden"
        id="home-language-popup"
        role="dialog"
        aria-modal="false"
        aria-labelledby="home-language-popup-title"
      >
        <div class="home-language-popup-header">
          <span id="home-language-popup-title">${escapeHtml(
            textFor(UI_TEXT.chooseLanguage),
          )}</span>
          <button
            class="home-language-close"
            id="home-language-close"
            type="button"
            aria-label="${escapeHtml(textFor(UI_TEXT.closeLanguage))}"
          >
            ×
          </button>
        </div>

        <div class="home-language-options">
          ${HOME_LANGUAGES.map(
            (language) => `
              <button
                class="home-language-option ${
                  language.id === activeHomeLanguage ? "active" : ""
                }"
                type="button"
                data-language="${language.id}"
                aria-pressed="${
                  language.id === activeHomeLanguage ? "true" : "false"
                }"
              >
                <span>${language.name}</span>
                <span class="home-language-check" aria-hidden="true">
                  ${language.id === activeHomeLanguage ? "✓" : ""}
                </span>
              </button>
            `,
          ).join("")}
        </div>
      </div>
    </div>
  `;
}

function getNewsForCategory(category: string): HomeNewsArticle[] {
  if (category === "top") {
    return HOME_NEWS_ARTICLES.filter((article) => article.category === "top");
  }

  return HOME_NEWS_ARTICLES.filter(
    (article) => article.category === category,
  );
}

function generateNewsCardsHtml(category = "top"): string {
  const categoryArticles = getNewsForCategory(category);
  const fallbackArticles = getNewsForCategory("top");
  const articles =
    categoryArticles.length > 0 ? categoryArticles : fallbackArticles;

  const featured =
    articles.find((article) => article.featured) ?? articles[0];

  const secondaryArticles = articles
    .filter((article) => article.id !== featured.id)
    .slice(0, 6);

  const fillArticles = HOME_NEWS_ARTICLES.filter(
    (article) =>
      article.id !== featured.id &&
      !secondaryArticles.some((item) => item.id === article.id),
  ).slice(0, Math.max(0, 6 - secondaryArticles.length));

  const cards = [...secondaryArticles, ...fillArticles].slice(0, 6);

  const featuredTitle = escapeHtml(textFor(featured.title));
  const featuredSummary = escapeHtml(textFor(featured.summary));

  return `
    <div class="home-news-layout">
      <a
        class="home-featured-story"
        href="${featured.url}"
        target="_blank"
        rel="noopener noreferrer"
      >
        <div class="home-featured-image-wrap">
          <img
            class="home-featured-image"
            src="${featured.image}"
            alt="${featuredTitle}"
            loading="eager"
          />
          <span class="home-featured-label">${escapeHtml(
            textFor(UI_TEXT.topStory),
          )}</span>
        </div>

        <div class="home-featured-content">
          <div class="home-story-source-row">
            <span class="home-story-source">${featured.source}</span>
            <span class="home-story-time">${escapeHtml(
              textFor(featured.time),
            )}</span>
          </div>

          <h3>${featuredTitle}</h3>
          <p>${featuredSummary}</p>
        </div>
      </a>

      <div class="home-news-card-grid">
        ${cards
          .map((article) => {
            const articleTitle = escapeHtml(textFor(article.title));
            const articleSummary = escapeHtml(textFor(article.summary));

            return `
              <a
                class="home-news-card"
                href="${article.url}"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  class="home-news-card-image"
                  src="${article.image}"
                  alt="${articleTitle}"
                  loading="lazy"
                />

                <div class="home-news-card-body">
                  <div class="home-story-source-row">
                    <span class="home-story-source">${article.source}</span>
                    <span class="home-story-time">${escapeHtml(
                      textFor(article.time),
                    )}</span>
                  </div>

                  <h3>${articleTitle}</h3>
                  <p>${articleSummary}</p>
                </div>
              </a>
            `;
          })
          .join("")}
      </div>
    </div>
  `;
}

function generateNewsFeedHtml(): string {
  return `
    <section class="home-discover-section" aria-labelledby="home-news-heading">
      <div class="home-discover-header">
        <div>
          <span class="home-discover-eyebrow">${escapeHtml(
            textFor(UI_TEXT.discover),
          )}</span>
          <h2 id="home-news-heading">${escapeHtml(
            textFor(UI_TEXT.newsForYou),
          )}</h2>
        </div>

      </div>

      <div class="home-topic-scroller" aria-label="News topics">
        <div class="home-topic-row" id="home-topic-row">
          ${generateTopicTabsHtml()}
        </div>
      </div>

      <div id="home-news-results">
        ${generateNewsCardsHtml(activeHomeTopic)}
      </div>
    </section>
  `;
}

export function renderHome() {
  const app = document.getElementById("app");
  if (!app) return;

  const {theme, applyTheme} = useTheme();
  const {user} = useAuth();

  applyTheme(theme);

  const isLoggedIn = Boolean(user?.email);
  const profileLetter = isLoggedIn ? user!.email[0].toUpperCase() : "";
  const profileColor = isLoggedIn ? getProfileColor(user!.email) : "";

  app.innerHTML = `
    <div class="home-wrapper">
      <nav class="top-nav desktop-nav">
        <div class="nav-left">
          <button>${escapeHtml(textFor(UI_TEXT.about))}</button>
          <button>${escapeHtml(textFor(UI_TEXT.store))}</button>
        </div>

        <div class="nav-right">
          <button class="theme-toggle-btn" id="theme-toggle">
            ${escapeHtml(textFor(UI_TEXT.theme))}: ${escapeHtml(
              getThemeLabel(theme),
            )}
          </button>
          <button>AMailMe</button>
          <button>${escapeHtml(textFor(UI_TEXT.images))}</button>

          <div class="apps-dropdown-container">
            <button class="apps-btn" id="apps-btn" type="button" aria-label="${escapeHtml(
              textFor(UI_TEXT.openApps),
            )}">
              <svg viewBox="0 0 24 24" width="24" height="24">
                <circle cx="5" cy="5" r="2.5"/><circle cx="12" cy="5" r="2.5"/>
                <circle cx="19" cy="5" r="2.5"/><circle cx="5" cy="12" r="2.5"/>
                <circle cx="12" cy="12" r="2.5"/><circle cx="19" cy="12" r="2.5"/>
                <circle cx="5" cy="19" r="2.5"/><circle cx="12" cy="19" r="2.5"/>
                <circle cx="19" cy="19" r="2.5"/>
              </svg>
            </button>
            ${generateAppsMenuHtml()}
          </div>

          <div class="profile-container" id="desktop-profile">
            ${
              isLoggedIn
                ? `<div class="profile-icon" style="background-color:${profileColor}" id="profile-icon">${profileLetter}</div>`
                : `<button class="signin-btn" id="signin-btn">${escapeHtml(
                    textFor(UI_TEXT.signIn),
                  )}</button>`
            }
          </div>
        </div>
      </nav>

      <nav class="mobile-nav">
        <div class="mobile-left">
          <button class="hamburger-btn" type="button" aria-label="${escapeHtml(
            textFor(UI_TEXT.openNavigation),
          )}">☰</button>
        </div>

        <div class="mobile-center">
          <button class="mobile-nav-btn active" type="button">${escapeHtml(
            textFor(UI_TEXT.all),
          )}</button>
          <button class="mobile-nav-btn" type="button">${escapeHtml(
            textFor(UI_TEXT.images),
          )}</button>
        </div>

        <div class="mobile-right">
          <div class="apps-dropdown-container">
            <button class="apps-btn" id="mobile-apps-btn" type="button" aria-label="${escapeHtml(
              textFor(UI_TEXT.openApps),
            )}">
              <svg viewBox="0 0 24 24" width="24" height="24">
                <circle cx="5" cy="5" r="2.5"/><circle cx="12" cy="5" r="2.5"/>
                <circle cx="19" cy="5" r="2.5"/><circle cx="5" cy="12" r="2.5"/>
                <circle cx="12" cy="12" r="2.5"/><circle cx="19" cy="12" r="2.5"/>
                <circle cx="5" cy="19" r="2.5"/><circle cx="12" cy="19" r="2.5"/>
                <circle cx="19" cy="19" r="2.5"/>
              </svg>
            </button>
          </div>

          <div class="profile-container" id="mobile-profile">
            ${
              isLoggedIn
                ? `<div class="profile-icon mobile-profile" style="background-color:${profileColor}" id="mobile-profile-icon">${profileLetter}</div>`
                : `<button class="signin-btn mobile-signin" id="mobile-signin-btn">${escapeHtml(
                    textFor(UI_TEXT.signIn),
                  )}</button>`
            }
          </div>
        </div>
      </nav>

      <main class="home-main">
        <h1 class="home-logo">AyeSearch</h1>

        <div class="home-search-wrapper" id="search-wrapper">
          <div class="search-bar">
            <span class="search-icon" aria-hidden="true">🔍</span>
            <input
              id="search-input"
              type="text"
              placeholder="${escapeHtml(textFor(UI_TEXT.searchPlaceholder))}"
              autocomplete="off"
              aria-label="${escapeHtml(textFor(UI_TEXT.searchPlaceholder))}"
            />
            <span class="mic-icon" aria-hidden="true">🎙️</span>
          </div>
          <div id="suggestions-container"></div>
        </div>

        <button class="ai-zone-btn" id="ai-zone-btn">AI Zone</button>

        <div class="action-buttons">
          <button id="search-btn">${escapeHtml(
            textFor(UI_TEXT.searchButton),
          )}</button>
          ${generateLanguagePickerHtml()}
        </div>

        ${generateNewsFeedHtml()}
      </main>

      <footer class="footer">
        <div class="footer-left">
          <button>Advertising</button>
          <button>Business</button>
        </div>

        <div class="footer-center">
          <span>Applying AI towards a future that is for us</span>
        </div>

        <div class="footer-right">
          <button>Privacy</button>
          <button>Terms</button>
          <button class="mobile-theme-btn" id="mobile-theme-btn">
            ${escapeHtml(textFor(UI_TEXT.theme))}: ${escapeHtml(
              getThemeLabel(theme),
            )}
          </button>
        </div>
      </footer>
    </div>
  `;

  setupHomeEvents();
}

function setupLanguageEvents(): void {
  const languageControl = document.getElementById("home-language-control");
  const languageButton = document.getElementById("home-language-btn");
  const languagePopup = document.getElementById("home-language-popup");
  const languageBackdrop = document.getElementById("home-language-backdrop");
  const languageClose = document.getElementById("home-language-close");

  const closeLanguagePicker = () => {
    languagePopup?.classList.add("hidden");
    languageBackdrop?.classList.add("hidden");
    languageButton?.setAttribute("aria-expanded", "false");
    document.body.classList.remove("home-language-modal-open");
  };

  const openLanguagePicker = () => {
    languagePopup?.classList.remove("hidden");
    languageBackdrop?.classList.remove("hidden");
    languageButton?.setAttribute("aria-expanded", "true");

    if (window.matchMedia("(max-width: 768px)").matches) {
      document.body.classList.add("home-language-modal-open");
    }
  };

  languageButton?.addEventListener("click", (event) => {
    event.stopPropagation();

    if (languagePopup?.classList.contains("hidden")) {
      openLanguagePicker();
    } else {
      closeLanguagePicker();
    }
  });

  languageClose?.addEventListener("click", closeLanguagePicker);
  languageBackdrop?.addEventListener("click", closeLanguagePicker);

  languagePopup
    ?.querySelectorAll<HTMLButtonElement>(".home-language-option")
    .forEach((button) => {
      button.addEventListener("click", () => {
        const language = button.dataset.language as HomeLanguage | undefined;

        if (!language || language === activeHomeLanguage) {
          closeLanguagePicker();
          return;
        }

        saveHomeLanguage(language);
        closeLanguagePicker();
        renderHome();
      });
    });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      closeLanguagePicker();
    }
  });

  document.addEventListener("click", (event) => {
    const target = event.target as Node;

    if (
      languageControl &&
      !languageControl.contains(target) &&
      !languagePopup?.classList.contains("hidden")
    ) {
      closeLanguagePicker();
    }
  });
}

function setupTopicEvents(): void {
  const topicButtons =
    document.querySelectorAll<HTMLButtonElement>(".home-topic-chip");
  const newsResults = document.getElementById("home-news-results");

  topicButtons.forEach((button) => {
    button.addEventListener("click", () => {
      const selectedTopic = button.dataset.topic ?? "top";

      if (selectedTopic === "wiki") {
        window.location.assign("https://aikipedia.ayesearch.com/");
        return;
      }

      activeHomeTopic = selectedTopic;

      topicButtons.forEach((topicButton) => {
        const isActive = topicButton === button;
        topicButton.classList.toggle("active", isActive);
        topicButton.setAttribute("aria-pressed", String(isActive));
      });

      if (!newsResults) return;

      newsResults.classList.add("is-refreshing");

      window.setTimeout(() => {
        newsResults.innerHTML = generateNewsCardsHtml(selectedTopic);
        newsResults.classList.remove("is-refreshing");
      }, 120);
    });
  });
}

function setupHomeEvents() {
  const {toggleTheme} = useTheme();

  const handleThemeToggle = () => {
    toggleTheme();
    window.setTimeout(renderHome, 50);
  };

  document
    .getElementById("theme-toggle")
    ?.addEventListener("click", handleThemeToggle);

  document
    .getElementById("mobile-theme-btn")
    ?.addEventListener("click", handleThemeToggle);

  const handleLogin = () => {
    const next = encodeURIComponent(window.location.href);

    window.location.assign(
      `https://api.ayesearch.com/auth/ayesearch/login/?next=${next}`,
    );
  };

  document.getElementById("signin-btn")?.addEventListener("click", handleLogin);

  document
    .getElementById("mobile-signin-btn")
    ?.addEventListener("click", handleLogin);

  const desktopIcon = document.getElementById("profile-icon");
  const mobileIcon = document.getElementById("mobile-profile-icon");

  const openProfile = (element: HTMLElement) => {
    renderProfileDropdown(element, () => {});
  };

  desktopIcon?.addEventListener("click", (event) => {
    event.stopPropagation();
    openProfile(desktopIcon);
  });

  mobileIcon?.addEventListener("click", (event) => {
    event.stopPropagation();
    openProfile(mobileIcon);
  });

  const appsBtn = document.getElementById("apps-btn");
  const mobileAppsBtn = document.getElementById("mobile-apps-btn");
  const appsPanel = document.getElementById("apps-dropdown-panel");
  const searchInput = document.getElementById(
    "search-input",
  ) as HTMLInputElement | null;
  const suggestionsContainer = document.getElementById(
    "suggestions-container",
  );

  const toggleAppsPanel = (event: Event) => {
    event.stopPropagation();

    if (!appsPanel || !suggestionsContainer) return;

    const isHidden = appsPanel.classList.contains("hidden");

    if (isHidden) {
      suggestionsContainer.innerHTML = "";
      appsPanel.classList.remove("hidden");

      if (mobileAppsBtn && event.currentTarget === mobileAppsBtn) {
        mobileAppsBtn.parentElement?.appendChild(appsPanel);
      } else if (appsBtn && event.currentTarget === appsBtn) {
        appsBtn.parentElement?.appendChild(appsPanel);
      }
    } else {
      appsPanel.classList.add("hidden");
    }
  };

  appsBtn?.addEventListener("click", toggleAppsPanel);
  mobileAppsBtn?.addEventListener("click", toggleAppsPanel);

  document.addEventListener("click", (event) => {
    const target = event.target as Node;

    if (
      appsPanel &&
      !appsPanel.contains(target) &&
      !appsBtn?.contains(target) &&
      !mobileAppsBtn?.contains(target)
    ) {
      appsPanel.classList.add("hidden");
    }

    const searchWrapper = document.getElementById("search-wrapper");

    if (
      suggestionsContainer &&
      searchWrapper &&
      !searchWrapper.contains(target)
    ) {
      suggestionsContainer.innerHTML = "";
    }
  });

  let searchTimeout: number | undefined;

  searchInput?.addEventListener("input", () => {
    if (searchTimeout !== undefined) {
      window.clearTimeout(searchTimeout);
    }

    searchTimeout = window.setTimeout(() => {
      appsPanel?.classList.add("hidden");

      if (!suggestionsContainer) return;

      renderHomeSearchSuggestions(
        searchInput.value,
        (value) => {
          searchInput.value = value;
          handleSearch(value);
        },
        suggestionsContainer,
      );
    }, 80);
  });

  searchInput?.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      handleSearch(searchInput.value);
    }
  });

  document.getElementById("search-btn")?.addEventListener("click", () => {
    handleSearch(searchInput?.value ?? "");
  });


  document.getElementById("ai-zone-btn")?.addEventListener("click", () => {
    renderAIZone();
  });

  setupTopicEvents();
  setupLanguageEvents();

}

function handleSearch(query: string) {
  const trimmed = query.trim();
  if (!trimmed) return;

  const normalized = normalizeQuery(trimmed);

  if (searchMap[normalized]) {
    window.location.href = searchMap[normalized];
    return;
  }

  window.location.href = `/search?q=${encodeURIComponent(trimmed)}`;
}
